import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import './DeployedDevices.css';
import { useTranslation } from "react-i18next";
import DataTable from "react-data-table-component";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faAdd, faChartSimple, faCircle, faCog, faExclamationTriangle, faKey, faMapPin, faRotate, faPencil, faTemperature0, faEur,
    faRadiation, faSearch, faTimes, faSatelliteDish
} from "@fortawesome/free-solid-svg-icons";
import { Button } from "react-bootstrap";
import { ButtonGroup } from "@mui/material";
import AddDeviceModal from "./Modals/AddDeviceModal";
import InformationModal from "./Modals/InfromationModal";
import { useDispatch } from "react-redux";
import { disableLoading, enableLoading } from "../../../features/loading/loadingSlice";
import GetDevicesQuery from "../../../queries/Devices/GetDevices";
import DeployedDevicesMap from "./DeployedDevicesMap/DeployedDevicesMap";
import withReactContent from "sweetalert2-react-content";
import swal from 'sweetalert2';
import AddApiKeyQuery from "../../../queries/ApiKey/AddApiKey";
import moment from "moment-timezone";
import { Link, useNavigate } from "react-router-dom";
import MaintenanceModal from "./Modals/MaintenanceModal";
import NukeModal from "./Modals/NukeModal";
import { toast } from 'react-toastify';
import WeatherMini from "../../Weather/Weather";

// Utility function to convert Roman numerals to integers
const romanToInt = (roman) => {
    const romanMap = { I: 1, V: 5, X: 10, L: 50, C: 100, D: 500, M: 1000 };
    let result = 0;
    for (let i = 0; i < roman.length; i++) {
        const current = romanMap[roman[i]];
        const next = romanMap[roman[i + 1]];
        if (current < next) {
            result -= current;
        } else {
            result += current;
        }
    }
    return result;
};

// Helper function to parse strings into comparable parts
const parseNameForSort = (str) => {
    if (!str) return [];
    const parts = [];
    let i = 0;
    
    while (i < str.length) {
        // Skip spaces and special characters, but remember position
        while (i < str.length && /[\s\-_.,]/.test(str[i])) {
            i++;
        }
        if (i >= str.length) break;
        
        // Check for regular numbers first (to avoid treating "I" as roman numeral when it's part of longer text)
        let j = i;
        while (j < str.length && /\d/.test(str[j])) {
            j++;
        }
        if (j > i) {
            parts.push({ type: 'number', value: parseInt(str.substring(i, j)) });
            i = j;
            continue;
        }
        
        // Check for Roman numerals (must be capital letters I, V, X, L, C, D, M)
        // BUT only if NOT followed by lowercase letters (to avoid treating "Chaidari" as roman C)
        j = i;
        while (j < str.length && /[IVXLCDM]/.test(str[j])) {
            j++;
        }
        if (j > i) {
            // Check if followed by lowercase letter - if so, it's part of a word, not a Roman numeral
            if (j < str.length && /[a-zäöüõ]/.test(str[j])) {
                // Not a Roman numeral, treat as text instead
                j = i;
                while (j < str.length && /[a-zA-ZäöüõÄÖÜÕáéíóúàèìòù]/.test(str[j])) {
                    j++;
                }
                if (j > i) {
                    parts.push({ type: 'text', value: str.substring(i, j).toLowerCase() });
                    i = j;
                    continue;
                }
            } else {
                // Valid Roman numeral (not followed by lowercase)
                const roman = str.substring(i, j);
                try {
                    const num = romanToInt(roman);
                    if (num > 0) {
                        parts.push({ type: 'roman', value: num });
                        i = j;
                        continue;
                    }
                } catch (e) {
                    // Not a valid Roman numeral, will be treated as regular letters
                }
            }
        }
        
        // Extract text (letters and accented characters)
        j = i;
        while (j < str.length && /[a-zA-ZäöüõÄÖÜÕáéíóúàèìòù]/.test(str[j])) {
            j++;
        }
        if (j > i) {
            parts.push({ type: 'text', value: str.substring(i, j).toLowerCase() });
            i = j;
            continue;
        }
        
        // Skip unknown character
        i++;
    }
    
    return parts;
};

// Natural sort comparator that handles Roman numerals
const naturalSort = (a, b) => {
    const aName = (a && a.plainName) ? String(a.plainName) : '';
    const bName = (b && b.plainName) ? String(b.plainName) : '';
    
    if (!aName && !bName) return 0;
    if (!aName) return 1;
    if (!bName) return -1;
    
    const aParts = parseNameForSort(aName);
    const bParts = parseNameForSort(bName);
    
    for (let i = 0; i < Math.min(aParts.length, bParts.length); i++) {
        const aPart = aParts[i];
        const bPart = bParts[i];
        
        // Both are text - use simple string comparison
        if (aPart.type === 'text' && bPart.type === 'text') {
            const aVal = aPart.value.toLowerCase();
            const bVal = bPart.value.toLowerCase();
            if (aVal < bVal) return -1;
            if (aVal > bVal) return 1;
        } 
        // Both are numbers or romans - compare numerically
        else if ((aPart.type === 'number' || aPart.type === 'roman') && (bPart.type === 'number' || bPart.type === 'roman')) {
            if (aPart.value < bPart.value) return -1;
            if (aPart.value > bPart.value) return 1;
        } 
        // Different types - text comes before numbers/romans
        else if (aPart.type !== bPart.type) {
            if (aPart.type === 'text') return -1;
            if (bPart.type === 'text') return 1;
        }
    }
    
    return aParts.length - bParts.length;
};

export default function DeployedDevices(props) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [currentUser] = useState(props.currentUser);
    const [currentGroup] = useState(props.currentGroup);
    const [addModalShow, setAddModalShow] = useState(false);
    const [informationModalShow, setInformationModalShow] = useState(false);
    const [nukeModalShow, setNukeModalShow] = useState(false);
    const [maintenanceModalShow, setMaintenanceModalShow] = useState(false);
    const [devices, setDevices] = useState();
    const [dataTableData, setDataTableData] = useState([]);
    const [selectedDevice, setSelectedDevice] = useState();

    // OTSINGU STATE
    const [filterText, setFilterText] = useState("");
    const [debouncedFilterText, setDebouncedFilterText] = useState("");
    const [groupFilter, setGroupFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState(null); // null | "online" | "offline" | "issues"
    const filterTimeoutRef = useRef(null);

    // Debounce filter text changes
    useEffect(() => {
        if (filterTimeoutRef.current) {
            clearTimeout(filterTimeoutRef.current);
        }
        filterTimeoutRef.current = setTimeout(() => {
            setDebouncedFilterText(filterText);
        }, 300);
        
        return () => {
            if (filterTimeoutRef.current) {
                clearTimeout(filterTimeoutRef.current);
            }
        };
    }, [filterText]);

    const apiKeyConfirmSwal = withReactContent(swal);
    const apiKeyErrorMessage = withReactContent(swal);
    const apiKeyConfirmMessage = withReactContent(swal);

    const navigate = useNavigate();

    // Helper function to extract first word(s) before numbers/roman numerals
    const getDeviceGroup = (name) => {
        if (!name) return '';
        // Remove a trailing standalone number/roman-numeral "word" and everything after it,
        // keeping only the leading text part. Word boundaries (\b) are required so that
        // names merely starting with a roman-numeral letter (e.g. "Valga", "Lihula") aren't
        // mistaken for a numeral and stripped entirely.
        return name.replace(/\s+\b(?:\d+|[IVXLCDM]+)\b.*$/, '').trim();
    };

    // --- SINU ORIGINAALFUNKTSIOONID (MUUTMATA) ---
    const active = (a, b) => {
        if (!a || !b) return t("Disabled");
        const isActive = moment().isBetween(moment(a), moment(b), null, '[]') || moment().isBefore(moment(a));
        const expiring = moment().isBetween(moment(b).subtract(1, 'month'), moment(b), null, '[]');
        const extended = moment(b).diff(moment(a), 'years') > 1;
        if (expiring) return t("Expiring");
        if (extended) return t("Extended");
        if (isActive) return t("Active");
        return t("Expired");
    }

    const mapModificationNames = (modification) => {
        switch (modification) {
            case "M": return t('M');
            case "S": return t('S');
            case "H_M": return t('Hybrid M');
            case "H_S": return t('Hybrid S');
            case "H_M_UPS": return t('Hybrid M (UPS)');
            case "H_S_UPS": return t('Hybrid S (UPS)');
            default: return t('Hybrid');
        }
    };

    const isDeviceOnlineMummu = (core_id, heartbeat) => {
        let online = false;
        let latestUptime = null;
        let now = moment(Date.now()).subtract(5, 'minutes');
        for (const [, value] of Object.entries(heartbeat)) {
            if (core_id === value.core_id) { latestUptime = value.updated_at; }
        }
        if (latestUptime && moment(now).isBefore(latestUptime)) { online = true; }
        return {
            "online": online,
            "latest_uptime": latestUptime ? moment(latestUptime).format('YYYY-MM-DD HH:mm') : t("Never"),
        };
    }

    const checkLastBlink = (lastBlinkDate) => {
        if (!lastBlinkDate) return false;
        const lastBlinkMoment = moment(lastBlinkDate, "YYYY-MM-DDTHH.mm.ss");
        return moment().diff(lastBlinkMoment, 'hours') <= 12;
    }

    // Kroonuaia's device only has a single (A) radar core — it has no B core,
    // so its B heartbeat/blink must be ignored instead of counted as a fault.
    const isSingleCoreDevice = (name) => !!name && name.toLowerCase().includes("kroonuaia");

    const getEffectiveHeartbeats = (device) => isSingleCoreDevice(device.name)
        ? device.device_heartbeats.slice(0, 1)
        : device.device_heartbeats;

    const drawOnlineBalls = (device) => {
        if (currentUser && currentGroup.id === 1) {
            return <div className="d-flex">
                {getEffectiveHeartbeats(device).map(i => (
                    <FontAwesomeIcon
                        key={i.core_id}
                        style={{ marginRight: '5px', fontSize: '10px' }}
                        title={t("Core: ") + i.core_id + " " + isDeviceOnlineMummu(i.core_id, device.device_heartbeats).latest_uptime}
                        icon={faCircle}
                        color={isDeviceOnlineMummu(i.core_id, device.device_heartbeats).online ? "green" : "red"} />
                ))}
            </div>
        }
        return null;
    }

    const putApiKeyToClipboard = (device_id) => {
        apiKeyConfirmSwal.fire({
            title: t("Are you sure?"),
            text: t("New one time api key will be generated"),
            icon: "warning",
            showCancelButton: true,
        }).then(res => {
            if (res.isConfirmed) {
                dispatch(enableLoading());
                AddApiKeyQuery(device_id).then((response) => {
                    dispatch(disableLoading());
                    if (response.status !== 200 && response.status !== 201) {
                        let message = response.response.data?.message;
                        apiKeyErrorMessage.fire({ title: <strong>{t("Error")}</strong>, html: <i>{t(message)}</i>, icon: 'error' });
                    } else {
                        let apiKey = response.data.data.api_key;
                        navigator.clipboard.writeText(apiKey);
                        apiKeyConfirmMessage.fire({
                            title: <strong>{t("API key")}</strong>,
                            html: <i>{apiKey}</i>,
                            showCancelButton: true,
                            cancelButtonText: <i>{t("Copy to clipboard")}</i>,
                            cancelButtonColor: "#4c78f4",
                            icon: 'warning'
                        }).then((res) => {
                            if (!res.isConfirmed) {
                                navigator.clipboard.writeText(apiKey);
                                toast.success(t("API key copied to clipboard"));
                            }
                        })
                    }
                });
            }
        });
    }



    // --- ANDMETE LAADIMINE ---
    const pollTimeoutRef = useRef(null);

    const fetchDevices = (noloader = false) => {
        // Cancel any pending poll so manual refreshes / modal closes don't spawn
        // additional, never-cancelled polling loops on top of the existing one.
        if (pollTimeoutRef.current) {
            clearTimeout(pollTimeoutRef.current);
            pollTimeoutRef.current = null;
        }
        if (!noloader) dispatch(enableLoading());
        GetDevicesQuery(currentGroup.id).then((response) => {
            pollTimeoutRef.current = setTimeout(() => { fetchDevices(true) }, 120000);
            if (response.status === 200) {
                let devicesRaw = response.data.data;
                setDevices(devicesRaw);
                let dataTableArray = [];

                for (const [, value] of Object.entries(devicesRaw)) {

                    let hasBlinked = isSingleCoreDevice(value.name)
                        ? checkLastBlink(value.last_blink_a)
                        : checkLastBlink(value.last_blink_a) && checkLastBlink(value.last_blink_b);

                    const effectiveHeartbeats = getEffectiveHeartbeats(value);
                    let hasHeartbeats = effectiveHeartbeats.length > 0;
                    let thisDeviceOnline = hasHeartbeats && effectiveHeartbeats.every(i => isDeviceOnlineMummu(i.core_id, value.device_heartbeats).online);
                    let thisDeviceAllRed = hasHeartbeats && effectiveHeartbeats.every(i => !isDeviceOnlineMummu(i.core_id, value.device_heartbeats).online);

                    // Fully functional = all bubbles green, nuke green, exclamation green.
                    // Offline = no bubbles at all, or all bubbles red + nuke red + exclamation red.
                    // Anything in between (some bubbles green/red mixed, or only one of
                    // nuke/exclamation red) counts as "issues".
                    let isFunctional = thisDeviceOnline && hasBlinked && !value.has_nuke;
                    let isOffline = !hasHeartbeats || (thisDeviceAllRed && value.has_nuke && !hasBlinked);
                    let isIssue = !isFunctional && !isOffline;

                    const maintenance = value.parent_maintenance ? value.parent_maintenance : value;
                    const maintenanceActive = active(maintenance.period_start, maintenance.period_end);

                    let color = "btn-info";
                    if (value.maintenance_disabled) color = "btn-primary";
                    else if (maintenanceActive === t("Active") || maintenanceActive === t("Extended")) color = "btn-success";
                    else if (maintenanceActive === t("Expired")) color = "btn-danger";
                    else if (maintenanceActive === t("Expiring")) color = "btn-warning";

                    dataTableArray.push({
                        id: value.id,
                        plainName: value.name, // Otsingu jaoks
                        group: getDeviceGroup(value.name),
                        online: thisDeviceOnline,
                        offline: isOffline,
                        hasIssue: isIssue,
                        functional: isFunctional,
                        status: drawOnlineBalls(value),
                        weather: <WeatherMini coordsString={value.coordinates} />,
                        name: <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span>{value.name}</span>
                            {value.radars > 0 && (
                                <span
                                    title={t("Radar count") + ": " + value.radars}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '3px',
                                        fontSize: '10px',
                                        fontWeight: 600,
                                        color: 'var(--bmc-text-muted, #6b7694)',
                                        background: 'var(--bmc-primary-light, #eef2ff)',
                                        borderRadius: '999px',
                                        padding: '2px 7px',
                                    }}
                                >
                                    <FontAwesomeIcon icon={faSatelliteDish} style={{ fontSize: '9px' }} />
                                    {value.radars}
                                </span>
                            )}
                        </div>,
                        modification: value.modification,
                        buttons: <ButtonGroup>
                            {(currentUser && currentGroup.id === 1) &&
                                <button type="button" className="btn btn-warning btn-icon-square-sm" onClick={() => putApiKeyToClipboard(value.id)} style={{ marginRight: "2px" }}><FontAwesomeIcon icon={faKey} /></button>}
                            <button type="button" className={`btn btn-icon-square-sm ${color}`} onClick={() => { setSelectedDevice(value); setMaintenanceModalShow(true); }} style={{ marginRight: "2px" }}><FontAwesomeIcon icon={faEur} /></button>
                            <button type="button" className="btn btn-primary btn-icon-square-sm" onClick={() => { setSelectedDevice(value); document.getElementById("deployed-map").scrollIntoView({ behavior: "smooth" }); }} style={{ marginRight: "2px" }}><FontAwesomeIcon icon={faMapPin} /></button>
                            {(currentUser && currentGroup.id === 1) && <>
                                <button type="button" className="btn btn-primary btn-icon-square-sm" onClick={() => { setSelectedDevice(value); setAddModalShow(true) }} style={{ marginRight: "2px" }}><FontAwesomeIcon icon={faPencil} /></button>
                                <button type="button" className={!value.has_nuke ? "btn btn-icon-square-sm btn-success" : "btn btn-icon-square-sm btn-danger btn-glow-danger"} onClick={() => { setSelectedDevice(value); setNukeModalShow(true); }} style={{ marginRight: "2px" }}><FontAwesomeIcon icon={faRadiation} /></button>
                            </>}
                            <button type="button" className={hasBlinked ? "btn btn-icon-square-sm btn-success" : "btn btn-icon-square-sm btn-danger btn-glow-danger"} onClick={() => { setSelectedDevice(value); setInformationModalShow(true); }} style={{ marginRight: "2px" }}><FontAwesomeIcon icon={faExclamationTriangle} /></button>
                            <button type="button" disabled={!hasHeartbeats} className="btn btn-primary btn-icon-square-sm" onClick={() => navigate("/sensors/" + value.id)} style={{ marginRight: "2px" }}><FontAwesomeIcon icon={faTemperature0} /></button>
                            {(currentUser && currentGroup.id === 1) && <button type="button" disabled={!hasHeartbeats} className="btn btn-primary btn-icon-square-sm" onClick={() => navigate("/device-settings/" + value.id)} style={{ marginRight: "2px" }}><FontAwesomeIcon icon={faCog} /></button>}
                            <button type="button" disabled={!hasHeartbeats} className="btn btn-primary btn-icon-square-sm" onClick={() => navigate("/statistics/" + value.id)} style={{ marginRight: "2px" }}><FontAwesomeIcon icon={faChartSimple} /></button>
                        </ButtonGroup>
                    });
                }

                // Not sorted here — filteredItems (below) sorts the data on every
                // render anyway, so sorting this full array too was redundant work.
                setDataTableData(dataTableArray);
            }

            if (!noloader) dispatch(disableLoading());
        }).catch(() => {
            if (!noloader) dispatch(disableLoading());
        });
    }

    useEffect(() => {
        fetchDevices();
        return () => {
            if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
        };
    }, []);

    // OTSINGU LOOGIKA
    // Only list groups with more than one device — a "group" that's really
    // just one device's own name (no shared siblings) isn't useful to filter by.
    const deviceGroups = useMemo(() => {
        const counts = {};
        dataTableData.forEach(item => {
            if (item.group) counts[item.group] = (counts[item.group] || 0) + 1;
        });
        return Object.keys(counts).filter(group => counts[group] > 1).sort();
    }, [dataTableData]);

    // Search + group filtered, but not yet narrowed by the status pills — the
    // pill counts below are computed from this so they reflect the search/group
    // filter without collapsing to whichever single status is currently active.
    const searchFilteredItems = useMemo(() => {
        return dataTableData.filter(item =>
            item.plainName &&
            item.plainName.toLowerCase().includes(debouncedFilterText.toLowerCase()) &&
            (!groupFilter || item.group === groupFilter)
        );
    }, [debouncedFilterText, groupFilter, dataTableData]);

    const statusCounts = useMemo(() => {
        return searchFilteredItems.reduce((acc, item) => {
            acc.total++;
            if (item.online) acc.online++;
            if (item.offline) acc.offline++;
            if (item.hasIssue) acc.issues++;
            if (item.functional) acc.functional++;
            return acc;
        }, { total: 0, online: 0, offline: 0, issues: 0, functional: 0 });
    }, [searchFilteredItems]);

    const filteredItems = useMemo(() => {
        const filtered = searchFilteredItems.filter(item =>
            !statusFilter ||
            (statusFilter === "online" && item.online) ||
            (statusFilter === "offline" && item.offline) ||
            (statusFilter === "issues" && item.hasIssue) ||
            (statusFilter === "functional" && item.functional)
        );
        // Sort by name - naturalSort works with row objects
        return filtered.sort((a, b) => naturalSort(a, b));
    }, [searchFilteredItems, statusFilter]);

    const toggleStatusFilter = (filter) => {
        setStatusFilter(prev => prev === filter ? null : filter);
    };

    const columns = [
        { 
            id: 'name',
            name: t('Name'), 
            selector: row => row.plainName, 
            sortable: true,
            cell: row => row.name 
        },
        (currentUser && currentGroup.id === 1) ? { name: t('Status'), cell: row => row.status, width: "120px" } : {},
        { name: t('Current weather'), cell: row => row.weather, width: "240px" },
        { name: t('Configuration'), selector: row => mapModificationNames(row.modification), sortable: true, width: "180px" },
        { name: '', cell: row => row.buttons, right: true, width: "280px" }
    ].filter(col => Object.keys(col).length > 0);

    return (<>
        <div className="row">
            <div className="col-12">
                <div className="card shadow-sm">
                    {/* HEADER - TOP LINE (BLUE) */}
                    <div className={(currentGroup && currentGroup.name === 'A1 Croatia') ? "card-header bg-primary a1" : "card-header bg-primary"} style={{ padding: '1rem 1.5rem' }}>
                        <div className="d-flex justify-content-between align-items-center">
                            <h4 className="card-title text-white mb-0">{t("Deployed devices")}</h4>
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                <button 
                                    type="button" 
                                    className="btn btn-light btn-icon-square-sm" 
                                    onClick={() => fetchDevices()}
                                    title={t("Refresh")}
                                    style={{ 
                                        width: '38px', 
                                        height: '38px',
                                        padding: '0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <FontAwesomeIcon icon={faRotate} />
                                </button>
                                {(currentUser && currentGroup.id === 1) &&
                                    <Button 
                                        className="btn btn-light"
                                        onClick={() => { setSelectedDevice(null); setAddModalShow(true) }}
                                        style={{
                                            height: '38px',
                                            padding: '0 1rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            fontSize: '0.875rem',
                                            fontWeight: '500'
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faAdd} style={{ marginRight: '0.5rem' }} /> {t("Add device")}
                                    </Button>
                                }
                            </div>
                        </div>
                    </div>

                    {/* HEADER - BOTTOM LINE (WHITE WITH FILTERS) */}
                    <div style={{ backgroundColor: 'var(--bmc-surface-alt, #f8f9fa)', padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--bmc-border, #dee2e6)' }}>
                        <div className="row g-3 align-items-center">
                            {/* SEARCH BOX */}
                            <div className="col-md-6">
                                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                    <FontAwesomeIcon 
                                        icon={faSearch} 
                                        style={{ 
                                            position: 'absolute', 
                                            left: '12px', 
                                            color: '#999',
                                            fontSize: '0.875rem',
                                            zIndex: 1
                                        }} 
                                    />
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder={`${t("Search")}...`}
                                        value={filterText}
                                        onChange={e => setFilterText(e.target.value)}
                                        style={{
                                            paddingLeft: '2.5rem',
                                            height: '38px',
                                            borderRadius: '0.375rem'
                                        }}
                                    />
                                    {filterText && (
                                        <FontAwesomeIcon 
                                            icon={faTimes} 
                                            onClick={() => setFilterText("")}
                                            style={{ 
                                                position: 'absolute', 
                                                right: '12px', 
                                                color: '#999',
                                                cursor: 'pointer',
                                                fontSize: '0.875rem'
                                            }} 
                                        />
                                    )}
                                </div>
                            </div>

                            {/* GROUP DROPDOWN */}
                            <div className="col-md-6">
                                <select 
                                    className="form-select" 
                                    style={{
                                        height: '38px',
                                        borderRadius: '0.375rem',
                                        paddingLeft: '0.75rem',
                                        paddingRight: '2.25rem'
                                    }}
                                    value={groupFilter}
                                    onChange={e => setGroupFilter(e.target.value)}
                                >
                                    <option value="">{t("All devices")}</option>
                                    {deviceGroups.map(group => (
                                        <option key={group} value={group}>{group}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="card-body">
                        <div className="table-responsive">
                            <DataTable 
                                columns={columns} 
                                data={filteredItems} 
                                sortable={false}
                                pagination 
                                paginationPerPage={100} 
                                paginationRowsPerPageOptions={[50, 100, 250]} 
                            />
                        </div>

                        {/* ILUS STATISTIKA RIDA */}
                        <div className="stats-footer-row mt-3">
                            <span
                                className={`stat-pill stat-pill-clickable${!statusFilter ? " active" : ""}`}
                                onClick={() => setStatusFilter(null)}
                            ><strong>{t("Total")}:</strong> {statusCounts.total}</span>
                            <span
                                className={`stat-pill stat-pill-clickable text-success${statusFilter === "online" ? " active" : ""}`}
                                onClick={() => toggleStatusFilter("online")}
                            ><strong>{t("Online")}:</strong> {statusCounts.online}</span>
                            <span
                                className={`stat-pill stat-pill-clickable text-danger${statusFilter === "offline" ? " active" : ""}`}
                                onClick={() => toggleStatusFilter("offline")}
                            ><strong>{t("Offline")}:</strong> {statusCounts.offline}</span>
                            <span
                                className={`stat-pill stat-pill-clickable text-warning${statusFilter === "issues" ? " active" : ""}`}
                                onClick={() => toggleStatusFilter("issues")}
                            ><strong>{t("Issues")}:</strong> {statusCounts.issues}</span>
                            <span
                                className={`stat-pill stat-pill-clickable text-success${statusFilter === "functional" ? " active" : ""}`}
                                onClick={() => toggleStatusFilter("functional")}
                            ><strong>{t("Fully functional")}:</strong> {statusCounts.functional}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <DeployedDevicesMap currentGroup={currentGroup} devices={devices} selectedDevice={selectedDevice} />

        <AddDeviceModal show={addModalShow} selectedDevice={selectedDevice} hideFn={(refresh) => { setAddModalShow(false); if (refresh) fetchDevices(); }} />
        <InformationModal isAdmin={currentUser && currentGroup.id === 1} show={informationModalShow} selectedDevice={selectedDevice} hideFn={() => setInformationModalShow(false)} />
        <NukeModal isAdmin={currentUser && currentGroup.id === 1} show={nukeModalShow} selectedDevice={selectedDevice} hideFn={() => setNukeModalShow(false)} />
        <MaintenanceModal show={maintenanceModalShow} selectedDevice={selectedDevice} currentGroup={currentGroup} currentUser={currentUser} devices={devices} hideFn={(refresh) => { setMaintenanceModalShow(false); if (refresh) fetchDevices(); }} />
    </>);
}